import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Dialog } from '@/components/Dialog/Dialog';
import { Select } from '@/components/Select/Select';
import { TextField } from '@/components/TextField/TextField';
import type { BrokerageRole } from '@/graphql/brokerages';
import {
  CONDITION_NAMES,
  type Grant,
  type GrantCondition,
  LEVEL_NAMES,
  levelFor,
  levelsOf,
  type PermissionLevel,
  RESOURCE_GROUPS,
  type ResourceEntry,
  withLevel,
} from '@/lib/brokerage-permissions';

import styles from './RoleEditor.module.css';

export interface RoleMemberOption {
  personId: string;
  label: string;
}

export interface RoleEditorValue {
  name: string;
  grants: Grant[];
  /** Who holds the role; only sent for a role this brokerage made itself. */
  assigneeIds: string[];
}

export interface RoleEditorProps {
  /** Null to create a new role. */
  role: BrokerageRole | null;
  /** Live members, to pick who holds a role this brokerage made itself. */
  members: readonly RoleMemberOption[];
  busy?: boolean;
  error?: string | null;
  onSubmit: (value: RoleEditorValue) => void;
  onDelete?: () => void;
  onCancel: () => void;
}

const CASE_CONDITIONS: readonly GrantCondition[] = ['ALWAYS', 'OWNING', 'COVERING'];

function levelOptions(entry: ResourceEntry): (readonly [string, string])[] {
  return [
    ['', 'No access'],
    ...levelsOf(entry).map((level) => [level, LEVEL_NAMES[level]] as const),
  ];
}

/** Name a role, choose what it allows in plain words, and who holds it. */
export function RoleEditor({
  role,
  members,
  busy = false,
  error = null,
  onSubmit,
  onDelete,
  onCancel,
}: RoleEditorProps) {
  const [name, setName] = useState(role?.name ?? '');
  const [grants, setGrants] = useState<Grant[]>(role?.grants.map((g) => ({ ...g })) ?? []);
  const [assigneeIds, setAssigneeIds] = useState<string[]>(role?.assigneeIds ?? []);
  const composed = role === null || role.presetKey === null;

  const set = (resource: string, condition: GrantCondition, level: string) =>
    setGrants((current) =>
      withLevel(current, resource, condition, (level || null) as PermissionLevel | null),
    );

  const toggleMember = (personId: string) =>
    setAssigneeIds((current) =>
      current.includes(personId) ? current.filter((id) => id !== personId) : [...current, personId],
    );

  return (
    <Dialog
      actions={
        <>
          {role && composed && onDelete ? (
            <Button disabled={busy} onClick={onDelete} variant="quiet">
              Delete role
            </Button>
          ) : null}
          <Button disabled={busy} onClick={onCancel} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={!name.trim()}
            loading={busy}
            onClick={() => onSubmit({ name: name.trim(), grants, assigneeIds })}
          >
            {role ? 'Save role' : 'Create role'}
          </Button>
        </>
      }
      description={
        composed
          ? 'Choose what people with this role can do, and who has it.'
          : 'A built-in role. Who has it follows each member’s main role.'
      }
      dismissable={!busy}
      onClose={onCancel}
      placement="drawer"
      title={role ? `Edit ${role.name ?? 'role'}` : 'New role'}
    >
      <div className={styles.form}>
        <TextField
          disabled={busy || !composed}
          hint={composed ? undefined : 'Built-in roles keep their name.'}
          label="Role name"
          onChange={(event) => setName(event.target.value)}
          value={name}
        />

        <Callout title="Where this applies">
          Keystone uses these permissions straight away. The main app follows them only for teams
          where Keystone permissions are switched on; elsewhere it still uses each member’s main
          role.
        </Callout>

        {RESOURCE_GROUPS.map((group) => (
          <fieldset className={styles.group} disabled={busy} key={group.title}>
            <legend className={styles.legend}>{group.title}</legend>
            {group.resources.map((entry) => (
              <div className={styles.resource} key={entry.key}>
                <div className={styles.resourceText}>
                  <span className={styles.resourceName}>{entry.name}</span>
                  <span className={styles.resourceMeta}>
                    View: {entry.read.toLowerCase()}. Edit: {entry.full.toLowerCase()}.
                  </span>
                </div>
                <div className={styles.levels}>
                  {(entry.perCase ? CASE_CONDITIONS : (['ALWAYS'] as const)).map((condition) => (
                    <Select
                      key={condition}
                      label={
                        entry.perCase ? `${entry.name}: ${CONDITION_NAMES[condition]}` : entry.name
                      }
                      onChange={(level) => set(entry.key, condition, level)}
                      options={levelOptions(entry)}
                      value={levelFor(grants, entry.key, condition) ?? ''}
                    />
                  ))}
                </div>
              </div>
            ))}
          </fieldset>
        ))}

        {composed ? (
          <fieldset className={styles.group} disabled={busy}>
            <legend className={styles.legend}>Who has this role</legend>
            {members.length === 0 ? (
              <p className={styles.empty}>Nobody is in this brokerage yet.</p>
            ) : (
              members.map((member) => (
                <label className={styles.member} key={member.personId}>
                  <input
                    checked={assigneeIds.includes(member.personId)}
                    onChange={() => toggleMember(member.personId)}
                    type="checkbox"
                  />
                  {member.label}
                </label>
              ))
            )}
          </fieldset>
        ) : null}

        {error ? (
          <Callout tone="danger" title="That didn't work">
            {error}
          </Callout>
        ) : null}
      </div>
    </Dialog>
  );
}
